import { useQuery } from "@tanstack/react-query";
import { TableCard } from "ui-components/Layout";
import { ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { orderDetailsStatusEnquiryColumns } from "./columns";
import { useState, useEffect } from "react";
import { useCustomerValue } from "providers/CustomerContext";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { getOrderDetailsStatusEnquiry, exportOrderDetailsAllCustomer } from "services/data.report.service";
import { CSVLink } from "react-csv";
import { FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, TextField } from "@mui/material";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import dayjs from "dayjs";
import { Error_ } from "ui-components/Alert";
import { LoadingDialog } from "ui-components/Alert";
import { useTeamValue } from "providers/TeamContext";
import { Success, Error } from "ui-components/Alert";
import { ExportButton } from "ui-components/Button";
import { EmailButton2 } from "ui-components/Button";
import { ExportDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import useFileDownloadHook from "hooks/useFileDownloadHook";

function OrderDetailsStatusEnquiry() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const date = dayjs(new Date()).format("YYYY-MM-DD");
  const sevenDaysBefore = dayjs(new Date()).subtract(1, "day").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(sevenDaysBefore);
  const [endDate, setEndDate] = useState(date);
  const [type, setType] = useState("ORDERDATE");
  const { isDownloading, downloadCsv } = useCsvDownloadHook();
  const { downloadFile, isDownloading: isExporting, error: exportError } = useFileDownloadHook();

  useEffect(() => {
    if (exportError) {
      setAlert(<Error_ content={exportError.message} onChange={setAlert} />);
    }
  }, [exportError]);

  const onExportAllCustomer = () => {
    if (!validateDateRange()) return;
    const filename = `ALLCUSTOMER_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_OrderDetails.xlsx`;
    downloadFile(() => exportOrderDetailsAllCustomer({ type, startDate, endDate }), filename);
  };

  const { refetch, isFetching } = useQuery({
    queryKey: ["order-details-status-enquiry", customerCode],
    queryFn: () => getOrderDetailsStatusEnquiry({ customerCode, type, startDate, endDate, custOrdNbrs }),
    enabled: false,
    onSuccess: (data) => {
      setData(data.data.data);
      setFilteredData(data.data.data);
    },
    onError: () => { },
  });
  const exportPermission =
    allowAll ||
    (Boolean(permissions["Order Details Status_Report"]) &&
      permissions["Order Details Status_Report"].findIndex((b) => b === "export") !== -1);

  const customProps = { startDate, setStartDate, endDate, setEndDate };

  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };

  const validateDateRange = () => {
    setAlert(null);
    const valid =
      dayjs(endDate).isSame(dayjs(startDate)) || dayjs(endDate).isAfter(dayjs(startDate));
    if (!valid) {
      setAlert(<Error_ content="Start date cannot greater than end date" onChange={setAlert} />);
      return false;
    }
    if (dayjs(date).isBefore(endDate)) {
      setAlert(<Error_ content="End date cannot greater than today date" onChange={setAlert} />);
      return false;
    }
    return true;
  };

  const onFetch = () => {
    if (!validateDateRange()) return;
    refetch();
  };
  const onSuccess = (data) => {
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => {
    setAlert(<Error data={data} onChange={setAlert} />);
  };

  const [custOrdNbrs, setCustOrdNbrs] = useState("");
  return (
    <ListLayout>
      {isFetching && <LoadingDialog />}
      {(isDownloading || isExporting) && <ExportDialog />}
      {alert && <MDBox sx={{ my: 3 }}>{alert}</MDBox>}
      <TableCard sx={{ my: 3 }}>
        <MDBox margin="1rem">
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Search Cust Ord Nbr (split by comma ,) - Max 10"
                fullWidth
                value={custOrdNbrs}
                onChange={(e) => setCustOrdNbrs(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <EmailButton2
                variant="gradient"
                color="info"
                filename={`${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_OrderDetailsStatus`}
                startDate={startDate}
                endDate={endDate}
                onSuccess={onSuccess}
                onError={onError}
                customerCode={customerCode}
                type={"order-detail-status"}
                dateType={type}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CustomDateRangePicker {...customProps} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDBox display="flex" flexWrap="wrap" alignItems="center" sx={{ gap: "1rem" }}>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => {
                    onFetch();
                  }}
                >
                  search
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={!exportPermission}
                  onClick={() => {
                    const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_OrderDetailsStatus`;
                    const data = filteredData.map((x) => {
                      const { skuPackType, zoneDescription, boxType, ...d } = x;
                      return { ...d, custOrdNbr: "'" + x.custOrdNbr, trackingNumber: x.trackingNumber + "'", referenceNo: x.referenceNo + "'" };
                    })
                    downloadCsv(filename, data);
                  }}
                >
                  export
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={!exportPermission}
                  onClick={() => {
                    onExportAllCustomer();
                  }}
                >
                  export all customer
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
          <Grid sx={{ paddingLeft: "12px" }}>
            <Grid item>
              <FormControl>
                <RadioGroup row onChange={(e) => setType(e.target.value)}>
                  <FormControlLabel
                    labelPlacement="left"
                    value="ORDERDATE"
                    label="Order Date"
                    control={
                      <Radio
                        sx={{ "& .MuiSvgIcon-root": { border: "1px solid #1A73E8" } }}
                        checked={type === "ORDERDATE"}
                      />
                    }
                  />
                  <FormControlLabel
                    labelPlacement="left"
                    value="DOCREATEDATE"
                    label="DO Create Date"
                    control={
                      <Radio
                        sx={{ "& .MuiSvgIcon-root": { border: "1px solid #1A73E8" } }}
                        checked={type === "DOCREATEDATE"}
                      />
                    }
                  />
                  <FormControlLabel
                    labelPlacement="left"
                    value="CANCELORDER"
                    label="Cancel Order"
                    control={
                      <Radio
                        sx={{ "& .MuiSvgIcon-root": { border: "1px solid #1A73E8" } }}
                        checked={type === "CANCELORDER"}
                      />
                    }
                  />
                </RadioGroup>
              </FormControl>
              <FormControl>
                <FormLabel
                  sx={{ color: "red", fontWeight: "bold", fontSize: "1rem", marginTop: ".5rem" }}
                >
                  * Click search button to load data
                </FormLabel>
              </FormControl>
            </Grid>
          </Grid>
        </MDBox>
        <BasicTable
          data={data}
          columns={orderDetailsStatusEnquiryColumns}
          onFilterRowsChange={handleFilterRowsChange}
        />
      </TableCard>
    </ListLayout>
  );
}

export default OrderDetailsStatusEnquiry;
